import { Block, Text, Inline, BLOCKS } from '@contentful/rich-text-types'
import { AtImageProps, MlRichTextProps, OrModalProductProps } from '@curran-catalog/curran-atomic-library'
import { FurnitureProductState } from 'context'
import {
  ContentfulObjFlooringSpecifications,
  ContentfulObjFlooringWeave,
  ContentfulObjFurnitureProduct,
  ContentfulObjFurnitureSpecifications,
  FlooringOrderButtonLabels,
} from 'types'
import { firstLetterCapitalize, formatPrice, getSpecsWeaveRichText, normalizeAsset } from 'utils'

const SPECIFICATIONS_CAPITALIZE_REGEX = /[A-Z][a-z]+|[0-9]+/g

export const getModalOrderInfo = (
  total: string,
  quantity: number,
  product: ContentfulObjFurnitureProduct,
  productState: FurnitureProductState,
  titles: {
    fabricTitle: string
    mainCushionsTitle: string
    addonsTitle: string
  },
) => {
  const infoProduct = {
    image: product.images
      ? (normalizeAsset({ asset: product.images[0] }) as AtImageProps)
      : ({ src: '/images/image-coming-soon.jpg', alt: '' } as AtImageProps),
    name: product.productName,
    price: total,
    quantity: quantity,
    extraInfo: normalizeExtraInfo(productState, product, titles),
  } as OrModalProductProps

  return infoProduct
}

export const normalizeExtraInfo = (
  productState: FurnitureProductState,
  product: ContentfulObjFurnitureProduct,
  titles: {
    fabricTitle: string
    mainCushionsTitle: string
    addonsTitle: string
  },
) => {
  const extraInfo = []
  productState.fabric && extraInfo.push({ title: titles.fabricTitle, text: productState.fabric?.fabricName })

  productState.presetFabricGrade &&
    extraInfo.push({
      title: titles.fabricTitle,
      text: productState?.presetFabricGrade?.fabricGradeName,
    })

  productState.primaryComboFrameSlingCol &&
    product.combos &&
    extraInfo.push({
      title: product.combos[0].comboName,
      text: productState.primaryComboFrameSlingCol?.frameSlingColName,
    })

  productState.secondaryComboFrameSlingCol &&
    product.combos &&
    extraInfo.push({
      title: product.combos[1].comboName,
      text: productState.secondaryComboFrameSlingCol?.frameSlingColName,
    })

  productState.tertiaryComboFrameSlingCol &&
    product.combos &&
    extraInfo.push({
      title: product.combos[2].comboName,
      text: productState.tertiaryComboFrameSlingCol?.frameSlingColName,
    })

  productState.quaternaryComboFrameSlingCol &&
    product.combos &&
    extraInfo.push({
      title: product.combos[3].comboName,
      text: productState.quaternaryComboFrameSlingCol?.frameSlingColName,
    })

  productState.mainCushionFabric &&
    extraInfo.push({ title: titles.mainCushionsTitle, text: productState.mainCushionFabric?.fabricName })

  productState.optionalCushionGroupFabrics?.forEach((optionalCushionGroupFabric) =>
    extraInfo.push({
      title: optionalCushionGroupFabric.cushionGroupName,
      text: `${optionalCushionGroupFabric.fabricName} x ${
        optionalCushionGroupFabric.hasQuantity && optionalCushionGroupFabric.quantity
          ? optionalCushionGroupFabric.quantity
          : productState.quantity
      }`,
    }),
  )

  productState.addonsFabricOrFrameSlingCol?.forEach((addon) =>
    extraInfo.push({
      title: addon.addonName,
      text: `${addon.quantity} x ${
        addon.price && addon.price >= 0 && addon.quantity ? formatPrice(addon.price / addon.quantity) : 0
      }`,
    }),
  )

  const addonGroup = productState.addonsGroupFabricOrFrameSlingCol
    ? Object.values(productState.addonsGroupFabricOrFrameSlingCol).reduce((acc, val) => acc.concat(val), [])
    : []

  addonGroup?.forEach((addon) =>
    extraInfo.push({
      title: addon.addonName,
      text: `${addon.quantity} x ${
        addon.price && addon.price >= 0 && addon.quantity ? formatPrice(addon.price / addon.quantity) : 0
      }`,
    }),
  )

  return extraInfo.length > 0 ? extraInfo : undefined
}

export const getSpecificationOptions = (
  specifications: ContentfulObjFurnitureSpecifications | ContentfulObjFlooringSpecifications | undefined,
): MlRichTextProps[] => {
  if (!specifications) return []

  const richTextSpecificationOptions = Object.entries(specifications)
    .map(([specFieldKey, specFieldValue]) => {
      if (
        !specFieldValue ||
        specFieldKey === 'CONTENTFUL_ID' ||
        specFieldKey === 'CONTENT_TYPE' ||
        specFieldKey === 'name' ||
        specFieldKey === 'UPDATED_AT' ||
        specFieldKey === '__typename' ||
        specFieldKey === 'sys'
      )
        return undefined

      const label = firstLetterCapitalize(specFieldKey)
        .split(/\s*Text\b/gi)[0]
        .match(SPECIFICATIONS_CAPITALIZE_REGEX)

      return {
        text: getSpecsWeaveRichText({ itemTitle: label ? label.join(' ') : specFieldKey, itemValue: specFieldValue }),
      }
    })
    .filter(Boolean)

  return richTextSpecificationOptions as MlRichTextProps[]
}

export const getSpecificationOptionsNodes = (
  specifications: ContentfulObjFurnitureSpecifications | ContentfulObjFlooringSpecifications | undefined,
) => {
  if (!specifications) return []

  const specs: (Block | Inline | Text)[] = []

  Object.entries(specifications).forEach(([specFieldKey, specFieldValue]) => {
    if (
      !specFieldValue ||
      specFieldKey === 'CONTENTFUL_ID' ||
      specFieldKey === 'CONTENT_TYPE' ||
      specFieldKey === 'name' ||
      specFieldKey === 'UPDATED_AT' ||
      specFieldKey === '__typename' ||
      specFieldKey === 'sys'
    )
      return undefined

    const label = firstLetterCapitalize(specFieldKey)
      .split(/\s*Text\b/gi)[0]
      .match(SPECIFICATIONS_CAPITALIZE_REGEX)

    specs.push(
      {
        nodeType: 'text',
        value: `${label ? label.join(' ') : specFieldKey}: `,
        marks: [{ type: 'bold' }],
        data: {},
      },
      {
        nodeType: 'text',
        value: `${specFieldValue}\n`,
        marks: [],
        data: {},
      },
    )
  })

  return specs
}

export const getWeaveSpecificationsNodes = (weave: ContentfulObjFlooringWeave): (Block | Inline | Text)[] => {
  const erpWeave: Record<string, string> = {}

  if (weave.erpWeaveAttributes) {
    weave.erpWeaveAttributes.forEach((weaveAttribute) => {
      const key = Object.keys(weaveAttribute)[0]
      if (key) {
        erpWeave[key] = Object.values(weaveAttribute)[0]
      }
    })
  }

  const makeSectionHeading = (title: string): Block => ({
    nodeType: BLOCKS.HEADING_3,
    data: { specSectionHeading: true },
    content: [
      {
        nodeType: 'text',
        value: title,
        marks: [],
        data: {},
      },
    ],
  })

  const makeSpecRow = (label: string, value: string): Block => ({
    nodeType: BLOCKS.PARAGRAPH,
    data: { specRow: true },
    content: [
      {
        nodeType: 'text',
        value: `${label}:`,
        marks: [{ type: 'bold' }],
        data: {},
      },
      {
        nodeType: 'text',
        value: value,
        marks: [],
        data: {},
      },
    ],
  })

  const productOverviewRows: Block[] = []
  const constructionRows: Block[] = []
  const performanceRows: Block[] = []

  if (erpWeave.usage) productOverviewRows.push(makeSpecRow('Recommended Use', erpWeave.usage))
  if (erpWeave.fiber) productOverviewRows.push(makeSpecRow('Pile Material', erpWeave.fiber))
  if (erpWeave.rollWidth) productOverviewRows.push(makeSpecRow('Available Widths', erpWeave.rollWidth))
  if (erpWeave.countryOfOrigin) productOverviewRows.push(makeSpecRow('Origin', erpWeave.countryOfOrigin))

  if (erpWeave.bindingOptions) constructionRows.push(makeSpecRow('Border Options', erpWeave.bindingOptions))
  if (weave.weaveStructureFamily) constructionRows.push(makeSpecRow('Production Method', weave.weaveStructureFamily))
  if (erpWeave.weaveWeight) constructionRows.push(makeSpecRow('Total Weight', erpWeave.weaveWeight))
  if (erpWeave.backing) constructionRows.push(makeSpecRow('Secondary Backing', erpWeave.backing))

  if (erpWeave.cleanability) performanceRows.push(makeSpecRow('Cleanability', erpWeave.cleanability))
  if (erpWeave.fireRating) performanceRows.push(makeSpecRow('Fire Rating', erpWeave.fireRating))

  const blocks: Block[] = []

  if (productOverviewRows.length) {
    blocks.push(makeSectionHeading('Product Overview'))
    blocks.push(...productOverviewRows)
  }

  if (constructionRows.length) {
    blocks.push(makeSectionHeading('Construction'))
    blocks.push(...constructionRows)
  }

  if (performanceRows.length) {
    blocks.push(makeSectionHeading('Performance'))
    blocks.push(...performanceRows)
  }

  return blocks
}

export const getOrderButtonLabel = (product: ContentfulObjFlooringWeave) => {
  if (product.callForQuote) return FlooringOrderButtonLabels.CALL_FOR_QUOTE
  if (product.supportsRugBuilder) return FlooringOrderButtonLabels.RUG_BUILDER
  return FlooringOrderButtonLabels.DEFAULT
}
